import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Package, Settings, CreditCard } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { PlanPricesEditor, type PlanPrice } from "./PlanPricesEditor";
import { DynamicListInput } from "@/components/agents/sections/DynamicListInput";
import { Label } from "@/components/ui/label";
import { ProductImageGallery } from "./ProductImageGallery";
import { useWorkspacePlan } from "@/hooks/useWorkspacePlan";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useGoogleCalendars } from "@/hooks/useGoogleCalendars";
import { isGcalMultiCalendarEnabled } from "@/lib/gcal-feature-flags";

const formSchema = z.object({
  name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  type: z.enum(["product", "service", "plan"], {
    required_error: "Selecione o tipo",
  }),
  description: z.string().optional(),
  // Campos apenas para produtos
  sku: z.string().optional(),
  stock: z.string().optional(),
  colors: z.string().optional(),
  dimensions: z.string().optional(),
  // Campos apenas para serviços
  duration: z.string().optional(),
  custom_duration: z.string().optional(),
  service_category: z.string().optional(),
  google_calendar_sync_calendar_id: z.string().nullable().optional(),
  // Campos para ambos (product/service)
  price: z.string().optional(),
  is_active: z.boolean(),
  // Plan-specific
  plan_billing_type: z.string().optional(),
  plan_trial_days: z.string().optional(),
  product_url: z.string().optional(),
  // Wholesale/reseller
  wholesale_enabled: z.boolean().optional(),
  wholesale_price: z.string().optional(),
  wholesale_min_quantity: z.string().optional(),
});

interface ProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product?: any;
  workspaceId: string;
}

export function ProductDialog({ open, onOpenChange, product, workspaceId }: ProductDialogProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { workspaceMetadata } = useWorkspace(user?.id);
  const { connections: gcalConnections, isLoading: gcalLoading } = useGoogleCalendars();
  const gcalMultiEnabled = isGcalMultiCalendarEnabled(workspaceMetadata);
  const allSyncCalendars = gcalConnections.flatMap((conn) => conn.syncCalendars);
  const enabledSyncCalendars = allSyncCalendars.filter((sc) => sc.is_enabled);
  const { productImagesAllowed } = useWorkspacePlan(workspaceId);
  const [planPrices, setPlanPrices] = useState<PlanPrice[]>([]);
  const [planFeatures, setPlanFeatures] = useState<string[]>([]);
  const [keywordsSynonyms, setKeywordsSynonyms] = useState<string[]>([]);
  const [measurements, setMeasurements] = useState<string[]>([]);
  const [wholesaleEnabled, setWholesaleEnabled] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      type: "product" as "product" | "service" | "plan",
      description: "",
      sku: "",
      price: "",
      stock: "",
      colors: "",
      dimensions: "",
      duration: "",
      custom_duration: "",
      service_category: "",
      google_calendar_sync_calendar_id: null,
      is_active: true,
      plan_billing_type: "",
      plan_trial_days: "",
      product_url: "",
      wholesale_enabled: false,
      wholesale_price: "",
      wholesale_min_quantity: "",
    },
  });

  const productType = form.watch("type");

  useEffect(() => {
    if (product) {
      const priceStr = product.price 
        ? product.price.toString().replace(".", ",") 
        : "";
      const colorsStr = Array.isArray(product.colors) 
        ? product.colors.join(", ") 
        : (product.colors || "");

      const predefinedDurations = [15, 30, 60, 90, 120, 180, 240, 480];
      const durationValue = product.duration;
      let durationField = durationValue ? durationValue.toString() : "";
      let customDurationField = "";

      if (durationValue && !predefinedDurations.includes(durationValue)) {
        durationField = "personalizado";
        customDurationField = durationValue.toString();
      }

      const wholesalePriceStr = product.wholesale_price 
        ? product.wholesale_price.toString().replace(".", ",") 
        : "";

      form.reset({
        name: product.name,
        type: product.type || "product",
        description: product.description || "",
        sku: product.sku || "",
        price: priceStr,
        stock: product.stock?.toString() || "",
        colors: colorsStr,
        dimensions: product.dimensions || "",
        duration: durationField,
        custom_duration: customDurationField,
        service_category: product.service_category || "",
        google_calendar_sync_calendar_id: product.google_calendar_sync_calendar_id ?? null,
        is_active: product.is_active,
        plan_billing_type: product.plan_billing_type || "",
        plan_trial_days: product.plan_trial_days?.toString() || "",
        product_url: product.product_url || "",
        wholesale_enabled: product.wholesale_enabled || false,
        wholesale_price: wholesalePriceStr,
        wholesale_min_quantity: product.wholesale_min_quantity?.toString() || "",
      });

      setWholesaleEnabled(product.wholesale_enabled || false);

      // Load plan features
      setPlanFeatures(Array.isArray(product.plan_features) ? product.plan_features : []);

      setKeywordsSynonyms(
        Array.isArray(product.keywords_synonyms) ? product.keywords_synonyms : [],
      );

      // Load measurements - fallback from dimensions
      if (Array.isArray(product.measurements) && product.measurements.length > 0) {
        setMeasurements(product.measurements);
      } else if (product.dimensions) {
        setMeasurements([product.dimensions]);
      } else {
        setMeasurements([]);
      }

      // Load plan prices
      if (product.type === "plan" && product.id) {
        (supabase as any)
          .from("product_plan_prices")
          .select("*")
          .eq("product_id", product.id)
          .order("order_index")
          .then(({ data }) => {
            if (data) {
              setPlanPrices(
                data.map((pp: any) => ({
                  id: pp.id,
                  period: pp.period,
                  period_label: pp.period_label || "",
                  price: pp.price?.toString().replace(".", ",") || "",
                  original_price: pp.original_price?.toString().replace(".", ",") || "",
                  is_highlighted: pp.is_highlighted || false,
                  custom_period_months: pp.custom_period_months?.toString() || "",
                  order_index: pp.order_index || 0,
                }))
              );
            } else {
              setPlanPrices([]);
            }
          });
      } else {
        setPlanPrices([]);
      }
    } else {
      form.reset({
        name: "",
        type: "product" as "product" | "service" | "plan",
        description: "",
        sku: "",
        price: "",
        stock: "",
        colors: "",
        dimensions: "",
        duration: "",
        custom_duration: "",
        service_category: "",
        google_calendar_sync_calendar_id: null,
        is_active: true,
        plan_billing_type: "",
        plan_trial_days: "",
        product_url: "",
        wholesale_enabled: false,
        wholesale_price: "",
        wholesale_min_quantity: "",
      });
      setPlanPrices([]);
      setPlanFeatures([]);
      setKeywordsSynonyms([]);
      setMeasurements([]);
      setWholesaleEnabled(false);
    }
  }, [product, form]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      // Converter preço do formato brasileiro (6.319,00) para formato de banco de dados (6319.00)
      let priceValue = null;
      if (values.price?.trim()) {
        // Remove pontos (separadores de milhares) e substitui vírgula por ponto (decimal)
        const priceStr = values.price.trim().replace(/\./g, "").replace(",", ".");
        priceValue = parseFloat(priceStr);
        
        if (isNaN(priceValue) || priceValue < 0) {
          toast({
            title: "Erro",
            description: "Preço inválido. Use o formato brasileiro (ex: 6.319,00 ou 199,99)",
            variant: "destructive",
          });
          return;
        }
      }

      // Converter cores de string para array (remover espaços extras)
      const colorsArray = values.colors
        ? values.colors.split(",").map(c => c.trim()).filter(c => c.length > 0)
        : null;

      // Parse wholesale price
      let wholesalePriceValue = null;
      if (values.wholesale_enabled && values.wholesale_price?.trim()) {
        const wpStr = values.wholesale_price.trim().replace(/\./g, "").replace(",", ".");
        wholesalePriceValue = parseFloat(wpStr);
        if (isNaN(wholesalePriceValue) || wholesalePriceValue < 0) {
          toast({
            title: "Erro",
            description: "Preço de atacado inválido.",
            variant: "destructive",
          });
          return;
        }
      }

      const productData: any = {
        name: values.name,
        type: values.type,
        description: values.description || null,
        price: priceValue,
        is_active: values.is_active,
        product_url: values.product_url?.trim() || null,
        wholesale_enabled: values.wholesale_enabled || false,
        wholesale_price: values.wholesale_enabled ? wholesalePriceValue : null,
        wholesale_min_quantity: values.wholesale_enabled && values.wholesale_min_quantity 
          ? parseInt(values.wholesale_min_quantity) 
          : null,
        keywords_synonyms: keywordsSynonyms.map((k) => k.trim()).filter(Boolean),
      };

      // Campos apenas para produtos
      if (values.type === "product") {
        productData.sku = values.sku || null;
        productData.stock = values.stock ? parseInt(values.stock) : null;
        productData.colors = colorsArray;
        productData.dimensions = values.dimensions || null;
        productData.measurements = measurements.filter(m => m.trim() !== "");
      }

      // Campos apenas para serviços
      if (values.type === "service") {
        let durationValue: number | null = null;

        if (values.duration === "personalizado") {
          if (!values.custom_duration || parseInt(values.custom_duration, 10) <= 0 || isNaN(parseInt(values.custom_duration, 10))) {
            toast({
              title: "Erro",
              description: "Informe a duração personalizada em minutos (valor maior que 0).",
              variant: "destructive",
            });
            return;
          }
          durationValue = parseInt(values.custom_duration, 10);
        } else if (values.duration) {
          durationValue = parseInt(values.duration, 10);

          if (isNaN(durationValue) || durationValue <= 0) {
            toast({
              title: "Erro",
              description: "Duração inválida. Selecione uma opção de duração válida.",
              variant: "destructive",
            });
            return;
          }
        }

        productData.duration = durationValue;
        productData.service_category = values.service_category || null;
        // Só tocar na coluna quando multi-agenda está on — evita quebrar save
        // se a migration Fase 2 ainda não foi aplicada (coluna inexistente).
        if (gcalMultiEnabled) {
          const serviceCalId = values.google_calendar_sync_calendar_id || null;
          if (
            serviceCalId &&
            !enabledSyncCalendars.some((sc) => sc.id === serviceCalId)
          ) {
            form.setError("google_calendar_sync_calendar_id", {
              type: "manual",
              message: "Escolha uma agenda habilitada ou herde a do agente.",
            });
            toast({
              title: "Agenda do serviço indisponível",
              description:
                "A agenda selecionada está desabilitada ou não existe. Reative-a em Integrações ou escolha outra — o agendamento não usa fallback silencioso para a agenda do agente.",
              variant: "destructive",
            });
            return;
          }
          productData.google_calendar_sync_calendar_id = serviceCalId;
        }
      } else if (gcalMultiEnabled) {
        productData.google_calendar_sync_calendar_id = null;
      }

      // Campos apenas para planos
      if (values.type === "plan") {
        productData.plan_features = planFeatures.filter(f => f.trim() !== "");
        productData.plan_trial_days = values.plan_trial_days ? parseInt(values.plan_trial_days) : null;
        productData.plan_billing_type = values.plan_billing_type || null;
      } else {
        productData.plan_features = null;
        productData.plan_trial_days = null;
        productData.plan_billing_type = null;
      }

      let savedProductId: string | null = null;

      if (product) {
        const { error } = await supabase
          .from("products")
          .update(productData)
          .eq("id", product.id);

        if (error) throw error;
        savedProductId = product.id;

        const typeLabels: Record<string, string> = { product: "Produto", service: "Serviço", plan: "Plano" };
        toast({
          title: `${typeLabels[values.type]} atualizado`,
          description: `${typeLabels[values.type]} foi atualizado com sucesso.`,
        });
      } else {
        const { data: insertedData, error } = await supabase.from("products").insert([{
          ...productData,
          workspace_id: workspaceId,
        }] as any).select("id").single();

        if (error) throw error;
        savedProductId = insertedData?.id;

        const typeLabels: Record<string, string> = { product: "Produto", service: "Serviço", plan: "Plano" };
        toast({
          title: `${typeLabels[values.type]} criado`,
          description: `${typeLabels[values.type]} foi criado com sucesso.`,
        });
      }

      // Save plan prices if type is plan
      if (values.type === "plan" && savedProductId) {
        // Delete existing prices
        await (supabase as any)
          .from("product_plan_prices")
          .delete()
          .eq("product_id", savedProductId);

        // Insert new prices
        const validPrices = planPrices.filter(p => p.price?.trim());
        if (validPrices.length > 0) {
          const pricesToInsert = validPrices.map((p, idx) => ({
            product_id: savedProductId,
            period: p.period,
            period_label: p.period_label || null,
            price: parseFloat(p.price.replace(/\./g, "").replace(",", ".")) || 0,
            original_price: p.original_price?.trim()
              ? parseFloat(p.original_price.replace(/\./g, "").replace(",", "."))
              : null,
            is_highlighted: p.is_highlighted,
            custom_period_months: p.period === "custom" && p.custom_period_months
              ? parseInt(p.custom_period_months)
              : null,
            order_index: idx,
          }));

          const { error: pricesError } = await (supabase as any)
            .from("product_plan_prices")
            .insert(pricesToInsert);

          if (pricesError) {
 console.error("Erro ao salvar preços do plano:", pricesError);
          }
        }
      }

      // Enqueue embedding for RAG (server-side via enqueue-product-embedding)
      if (savedProductId) {
        try {
 console.log(" Enfileirando embedding para produto:", savedProductId);
          await supabase.functions.invoke("enqueue-product-embedding", {
            body: { productId: savedProductId },
          });
 console.log(" Produto enfileirado para embedding");
        } catch (embeddingError) {
 console.warn(" Falha ao enfileirar embedding:", embeddingError);
          toast({
            title: "Atenção",
            description: "Produto salvo, mas indexação para IA ficou pendente. Tente reindexar depois.",
            variant: "default",
          });
        }
      }

      onOpenChange(true);
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[95vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>{product ? "Editar Item" : "Novo Item"}</DialogTitle>
          <DialogDescription>
            {product
              ? "Atualize as informações do item"
              : "Adicione um novo produto ou serviço ao catálogo"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        {field.value ? (
                          <div className="flex items-center gap-2">
                            {field.value === "product" ? (
                              <>
                                <Package className="w-4 h-4" />
                                <span>Produto</span>
                              </>
                            ) : field.value === "plan" ? (
                              <>
                                <CreditCard className="w-4 h-4" />
                                <span>Plano</span>
                              </>
                            ) : (
                              <>
                                <Settings className="w-4 h-4" />
                                <span>Serviço</span>
                              </>
                            )}
                          </div>
                        ) : (
                          <SelectValue placeholder="Selecione o tipo" />
                        )}
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="product">
                        <span className="flex items-center gap-2">
                          <Package className="w-4 h-4" />
                          <span>Produto</span>
                        </span>
                      </SelectItem>
                      <SelectItem value="service">
                        <span className="flex items-center gap-2">
                          <Settings className="w-4 h-4" />
                          <span>Serviço</span>
                        </span>
                      </SelectItem>
                      <SelectItem value="plan">
                        <span className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4" />
                          <span>Plano</span>
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl>
                    <Input placeholder="Plano Starter" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={productType === "product" ? "Descrição do produto..." : "Descrição do serviço..."}
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <Label className="text-sm font-medium">Palavras-chave / Sinônimos</Label>
              <p className="text-xs text-muted-foreground">
                Gírias, apelidos e termos que clientes usam ao pedir este{" "}
                {productType === "service"
                  ? "exame ou serviço"
                  : productType === "plan"
                    ? "plano"
                    : "produto"}
                . A IA usa na busca semântica para não se perder no contexto.
              </p>
              <DynamicListInput
                items={keywordsSynonyms}
                onChange={setKeywordsSynonyms}
                placeholder={
                  productType === "service"
                    ? "Ex.: hemograma, exame de sangue"
                    : "Ex.: tênis, sneaker, tênis casual"
                }
                addLabel="Adicionar termo"
                emptyMessage="Nenhum sinônimo cadastrado"
              />
            </div>

            {/* Wholesale toggle - for products and services */}
            {productType !== "plan" && (
              <div className="space-y-3 rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Preço de Revenda/Atacado</Label>
                    <p className="text-xs text-muted-foreground">
                      Ativar preço diferenciado para revendedores
                    </p>
                  </div>
                  <FormField
                    control={form.control}
                    name="wholesale_enabled"
                    render={({ field }) => (
                      <Switch
                        checked={wholesaleEnabled}
                        onCheckedChange={(checked) => {
                          setWholesaleEnabled(checked);
                          field.onChange(checked);
                        }}
                      />
                    )}
                  />
                </div>
                {wholesaleEnabled && (
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <FormField
                      control={form.control}
                      name="wholesale_price"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Preço Atacado (R$)</FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              placeholder="149,99"
                              {...field}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^\d,.-]/g, "");
                                field.onChange(value);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="wholesale_min_quantity"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Qtd. Mínima</FormLabel>
                          <FormControl>
                            <Input type="number" placeholder="10" {...field} />
                          </FormControl>
                          <p className="text-xs text-muted-foreground">Opcional</p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Preço base - não exibir para planos (usam preços por período) */}
            {productType !== "plan" && (
              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Preço (R$)</FormLabel>
                    <FormControl>
                      <Input 
                        type="text" 
                        placeholder="199,99" 
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^\d,.-]/g, "");
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      Use vírgula ou ponto como separador decimal
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* CAMPOS APENAS PARA PRODUTOS */}
            {productType === "product" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="sku"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>SKU</FormLabel>
                        <FormControl>
                          <Input placeholder="PROD-001" {...field} />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          Código único do produto
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="stock"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estoque</FormLabel>
                        <FormControl>
                          <Input type="number" placeholder="999" {...field} />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          Quantidade em estoque
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="colors"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cores Disponíveis</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Bege Claro, Grafite, Laranja, Verde" 
                          {...field} 
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        Separe as cores por vírgula (ex: Bege Claro, Grafite, Laranja)
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Medidas/Dimensões</Label>
                  <DynamicListInput
                    items={measurements}
                    onChange={setMeasurements}
                    placeholder="Ex: 200cm x 90cm x 85cm"
                    addLabel="Adicionar medida"
                    emptyMessage="Nenhuma medida adicionada"
                    maxItems={20}
                    showNumbers={false}
                  />
                  <p className="text-xs text-muted-foreground">
                    Adicione múltiplas medidas do produto (ex: 200cm x 90cm x 85cm)
                  </p>
                </div>
              </>
            )}

            {/* CAMPOS APENAS PARA SERVIÇOS */}
            {productType === "service" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="duration"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Duração do Serviço</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a duração" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="15">15 minutos</SelectItem>
                            <SelectItem value="30">30 minutos</SelectItem>
                            <SelectItem value="60">1 hora</SelectItem>
                            <SelectItem value="90">1h30</SelectItem>
                            <SelectItem value="120">2 horas</SelectItem>
                            <SelectItem value="180">3 horas</SelectItem>
                            <SelectItem value="240">4 horas</SelectItem>
                            <SelectItem value="480">1 dia</SelectItem>
                            <SelectItem value="personalizado">Personalizado</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Tempo estimado de duração do serviço
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="service_category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Categoria do Serviço</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a categoria" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="consultoria">💼 Consultoria</SelectItem>
                            <SelectItem value="suporte-tecnico">🛠️ Suporte Técnico</SelectItem>
                            <SelectItem value="design">🎨 Design</SelectItem>
                            <SelectItem value="desenvolvimento">💻 Desenvolvimento</SelectItem>
                            <SelectItem value="marketing">📢 Marketing</SelectItem>
                            <SelectItem value="treinamento">🎓 Treinamento</SelectItem>
                            <SelectItem value="manutencao">🔧 Manutenção</SelectItem>
                            <SelectItem value="instalacao">⚙️ Instalação</SelectItem>
                            <SelectItem value="outro">📋 Outro</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Categoria do serviço oferecido
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {form.watch("duration") === "personalizado" && (
                  <FormField
                    control={form.control}
                    name="custom_duration"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Duração personalizada (minutos)</FormLabel>
                        <FormControl>
                          <Input type="number" min="1" placeholder="Ex: 45" {...field} />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          Informe a duração do serviço em minutos
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {gcalMultiEnabled && (
                  <FormField
                    control={form.control}
                    name="google_calendar_sync_calendar_id"
                    render={({ field }) => {
                      const selectedId = field.value ?? null;
                      const selectedEnabled = selectedId
                        ? enabledSyncCalendars.find((sc) => sc.id === selectedId)
                        : null;
                      const selectedDisabled =
                        selectedId && !selectedEnabled
                          ? allSyncCalendars.find((sc) => sc.id === selectedId)
                          : null;

                      return (
                        <FormItem>
                          <FormLabel>Agenda deste serviço</FormLabel>
                          <Select
                            onValueChange={(v) => field.onChange(v === "__inherit__" ? null : v)}
                            value={field.value ?? "__inherit__"}
                            disabled={
                              gcalLoading ||
                              (enabledSyncCalendars.length === 0 && !selectedDisabled)
                            }
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={
                                    gcalLoading
                                      ? "Carregando agendas..."
                                      : enabledSyncCalendars.length === 0 && !selectedDisabled
                                        ? "Nenhuma agenda habilitada"
                                        : "Herdar do agente"
                                  }
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="__inherit__">
                                Herdar do agente (agenda padrão)
                              </SelectItem>
                              {selectedDisabled && (
                                <SelectItem value={selectedDisabled.id}>
                                  {selectedDisabled.display_name || selectedDisabled.calendar_id}
                                  {" (desabilitada)"}
                                </SelectItem>
                              )}
                              {enabledSyncCalendars.map((sc) => (
                                <SelectItem key={sc.id} value={sc.id}>
                                  {sc.display_name || sc.calendar_id}
                                  {sc.is_primary ? " (primária)" : ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {selectedDisabled && (
                            <p className="text-xs text-amber-700 dark:text-amber-300">
                              Esta agenda está desabilitada em Integrações. Reative-a ou escolha outra
                              (ou “Herdar do agente”). Com ela desabilitada, disponibilidade/create
                              falham — não há fallback para a agenda padrão do agente.
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            Opcional. Use quando este serviço pertence a um profissional/equipe com agenda
                            própria. Se vazio, usa a agenda padrão do agente. A agenda deve estar no
                            allowlist do Agente; caso contrário disponibilidade/create falham com erro
                            claro. O expediente continua do agente.
                          </p>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />
                )}
              </>
            )}

            {/* CAMPOS APENAS PARA PLANOS */}
            {productType === "plan" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="plan_billing_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tipo de Cobrança</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="recurring">🔄 Recorrente</SelectItem>
                            <SelectItem value="one_time">💳 Pagamento Único</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="plan_trial_days"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dias de Trial</FormLabel>
                        <FormControl>
                          <Input type="number" min="0" placeholder="Ex: 7" {...field} />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          Período de teste gratuito (opcional)
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <PlanPricesEditor prices={planPrices} onChange={setPlanPrices} />

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Benefícios do Plano</Label>
                  <DynamicListInput
                    items={planFeatures}
                    onChange={setPlanFeatures}
                    placeholder="Ex: Acesso ilimitado"
                    addLabel="Adicionar benefício"
                    emptyMessage="Nenhum benefício adicionado"
                    maxItems={20}
                    showNumbers={false}
                  />
                </div>
              </>
            )}

            <FormField
              control={form.control}
              name="product_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Link do Produto</FormLabel>
                  <FormControl>
                    <Input placeholder="https://www.exemplo.com/produto" {...field} />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    URL que o agente pode compartilhar com o cliente
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Ativo</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Itens inativos não aparecem para os clientes
                    </p>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            {product?.id && (
              <div className="border-t pt-4">
                <ProductImageGallery
                  productId={product.id}
                  workspaceId={workspaceId}
                  planBlocked={!productImagesAllowed}
                />
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit">{product ? "Atualizar" : "Criar"}</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
